import { useState } from "react";
import { SkyInputNative } from "@/components/forms/SkyInputNative";
import { ChevronDown, Check, Search } from "lucide-react";
import { SkyPopover } from "@skypointcloud/skypoint-ui";
import { usePacks } from "../../../components/packs/PackContext";
import { useTerm } from "../../../hooks/useTerm";
import { useSelectedCommunity } from "../../../hooks/useSelectedCommunity";

/**
 * GLOBAL community selector for the dark top header. SALES-SIDE ONLY:
 *
 *   VISIBILITY CONTRACT - renders IFF usePacks().currentMode === "operator_crm" (Sales);
 *   returns null (ABSENT, not greyed) on Marketing/Advisor, mirroring ModeToggle's gate.
 *
 * Trigger: a Transparent-appearance pill matched to the ModeToggle on the dark bar. Menu: a
 * searchable list (mirrors the Tenant/Instance SkyDropdown) - search box + filtered list +
 * amber checkmark on the selected, in the design-system dropdown-menu surface. Menu width is
 * measured from the trigger so it drops directly under the pill, and the list scrolls inside.
 *
 * Slice-backed (useSelectedCommunity) so the selection persists across navigation; off the sales
 * side we pass no instanceId so the CRM-only accessible-communities query is SKIPPED. It only
 * LISTS communities and SETS the selection - it does not filter data or drive tz (later rounds).
 */
function MenuRow({
  label,
  selected,
  onClick,
}: {
  label: string;
  selected: boolean;
  onClick: () => void;
}) {
  return (
    <button
      type="button"
      onClick={onClick}
      className="flex w-full items-center gap-2 min-h-8 px-2 py-1.5 rounded-md text-left text-sm text-gray-900 hover:bg-gray-100"
    >
      {selected ? (
        <Check className="w-4 h-4 text-amber-500 shrink-0" />
      ) : (
        <span className="w-4 h-4 shrink-0" />
      )}
      <span className="truncate">{label}</span>
    </button>
  );
}

export function GlobalCommunitySelector({ instanceId }: { instanceId: string | undefined }) {
  const { currentMode } = usePacks();
  // #1064 — every noun in this control is tenant-renameable terminology, the same
  // seam the CRM sidebar already uses (packNav.ts). Hardcoded literals here are
  // why a tenant that renamed "Community" saw the rename in the left nav but not
  // in the dark top bar. Defaults resolve to the PLATFORM_TERMS values, so the
  // rendered strings are unchanged for a tenant with no override.
  const term = useTerm();
  const communityTerm = term("community.singular");
  const communitiesTerm = term("community.plural").toLowerCase();
  const allCommunitiesLabel = `All ${communitiesTerm}`;
  const isSales = currentMode === "operator_crm";
  const { selectedCommunityId, setSelectedCommunity, communities } = useSelectedCommunity(
    isSales ? instanceId : undefined,
  );
  const [open, setOpen] = useState(false);
  const [query, setQuery] = useState("");

  const selectedName = selectedCommunityId
    ? (communities.find((c) => c.id === selectedCommunityId)?.name ?? communityTerm)
    : allCommunitiesLabel;

  if (!isSales) return null; // sales-only visibility contract

  const pick = (id: string | null) => {
    setSelectedCommunity(id);
    setOpen(false);
    setQuery("");
  };

  const q = query.trim().toLowerCase();
  const filtered = q ? communities.filter((c) => c.name.toLowerCase().includes(q)) : communities;

  return (
    <SkyPopover
      open={open}
      onOpenChange={(o) => {
        setOpen(o);
        if (!o) setQuery("");
      }}
      placement="bottom-end"
      className="!w-64 !min-w-0 !max-w-none"
      trigger={
        <button
          type="button"
          aria-label={`Filter by ${communityTerm.toLowerCase()}`}
          onClick={() => setOpen((v) => !v)}
          className="inline-flex items-center gap-2 h-8 w-64 pl-3 pr-2.5 rounded-md border border-white/20 bg-white/5 text-white text-sm font-semibold whitespace-nowrap transition-colors hover:bg-white/10 focus:outline-none focus-visible:ring-2 focus-visible:ring-white/80"
        >
          {/* The eyebrow was `shrink-0` back when it was the literal "Community".
              Now that it renders a tenant-renameable term (#1064), a long term
              would eat the whole 256px track: the value span collapsed to zero
              and the chevron was clipped out of the pill. It must therefore
              TRUNCATE rather than hold its width.

              `truncate` (not `shrink-0` removal alone) is what actually does it:
              a flex item's `min-width: auto` floors it at min-content, and the
              button's inherited `whitespace-nowrap` makes min-content the whole
              string — so simply dropping `shrink-0` measures byte-identical to
              keeping it. `truncate`'s `overflow: hidden` is what zeroes the
              automatic minimum size (hence no separate `min-w-0` is needed).

              The value span then carries a `min-w-16` floor so the SELECTION —
              the thing the control actually communicates — can never be starved
              to zero by a long term. Verified by layout measurement, not by eye:
              chevron inside clientWidth and value width > 0 for a default,
              realistic, extreme and absurd term. See GlobalCommunitySelector's
              layout-contract tests. */}
          <span className="text-white/55 font-medium truncate">{communityTerm}</span>
          <span className="flex-1 truncate text-left min-w-16">{selectedName}</span>
          <ChevronDown className="w-4 h-4 text-white/70 shrink-0" />
        </button>
      }
    >
      {/* No manual width: SkyPopover sizes the content wrapper to the trigger via
          --radix-popover-trigger-width, so the menu matches the pill exactly. */}
      <div className="w-full">
        {/* Search (like the Tenant/Instance SkyDropdown) */}
        <div className="p-2 pb-1">
          {/* The magnifier goes in SkyInput's own contentBefore slot rather
              than being absolutely positioned over a padded field, so the
              icon and the text can no longer drift apart. */}
          <SkyInputNative
            type="text"
            autoFocus
            className="w-full"
            contentBefore={<Search className="w-4 h-4" />}
            value={query}
            onChange={(e) => setQuery(e.target.value)}
            placeholder="Search..."
            aria-label={`Search ${communitiesTerm}`}
          />
        </div>
        {/* List */}
        <div className="max-h-72 overflow-y-auto overscroll-contain px-1 pb-1">
          {!q && (
            <MenuRow
              label={allCommunitiesLabel}
              selected={selectedCommunityId === null}
              onClick={() => pick(null)}
            />
          )}
          {filtered.map((c) => (
            <MenuRow
              key={c.id}
              label={c.name}
              selected={selectedCommunityId === c.id}
              onClick={() => pick(c.id)}
            />
          ))}
          {q && filtered.length === 0 && (
            <div className="px-2 py-2 text-sm text-gray-400">No {communitiesTerm} found</div>
          )}
        </div>
      </div>
    </SkyPopover>
  );
}
